/**
 * Точечные getById для /orders: разбор WS-батча (что добирать, что
 * удалять), правила применения ответа, очередь с лимитом параллельности.
 * Чистый модуль без PRPC/React, чтобы Jest гонял те же последовательности
 * событий, что ловит страница.
 */

import { ModelEventAction, ModelSubscribeEvent } from "./ModelEventConstructor";

export type StaleOrderSnapshot = {
	id?: number | string;
	status?: string;
	closedAt?: unknown;
	offer?: { orderOfferTime?: number; serverTime?: number } | null;
	executingStages?: { accepted?: unknown } | null;
	executorToOrder?: unknown[] | null;
};

/**
 * Закрытый заказ не живёт в не-closed табе: сервер шлёт на него delete,
 * но update может прийти позже delete (разные NATS-субъекты).
 */
export const isGoneOrderSnapshot = (
	raw: StaleOrderSnapshot | null | undefined,
	isClosedTab: boolean,
): boolean =>
	!raw ||
	(!isClosedTab && (raw.status === "closed" || Boolean(raw.closedAt)));

/** Пустой update/merge сервер шлёт, когда снапшот надо добрать по id. */
const HYDRATABLE_ACTIONS = new Set<ModelEventAction>([
	ModelEventAction.UPDATE,
	ModelEventAction.MERGE,
]);

/**
 * add/replace в ModelEventConstructor уменьшают счётчик ожидаемых
 * INIT-гидраций и ставят строку по index, поэтому их не подменяем,
 * а дописываем delete следом в том же батче.
 */
const INSERT_ACTIONS = new Set<ModelEventAction>([
	ModelEventAction.ADD,
	ModelEventAction.REPLACE,
]);

export type SplitOrderEvents = {
	/** update/merge без данных — надо добрать через getById */
	hydrateIds: number[];
	/** события для ModelEventConstructor */
	immediate: ModelSubscribeEvent[];
	/** по этим id в батче есть delete (в том числе дописанный) */
	deletedIds: number[];
	/**
	 * Батч очищает окно: INIT / regenerateState. `regenerateIndexes` сюда
	 * не входит — он не трогает выжившие строки, а шлёт явные delete/add,
	 * и конструктор при неизменном окне не эмитит.
	 */
	windowReset: boolean;
};

const WINDOW_RESET_ACTIONS = new Set<ModelEventAction>([
	ModelEventAction.INIT,
	ModelEventAction.STATE,
]);

export const splitOrderEvents = (
	events: ModelSubscribeEvent[] | undefined,
	isClosedTab: boolean,
): SplitOrderEvents => {
	const hydrateIds: number[] = [];
	const immediate: ModelSubscribeEvent[] = [];
	const deletedIds: number[] = [];
	let windowReset = false;
	for (const event of events ?? []) {
		const hasId = Boolean(event?.id);
		if (WINDOW_RESET_ACTIONS.has(event?.action)) windowReset = true;
		if (hasId && event.action === ModelEventAction.DELETE) {
			deletedIds.push(Number(event.id));
		}
		if (hasId && HYDRATABLE_ACTIONS.has(event.action)) {
			if (event.data == null) {
				hydrateIds.push(Number(event.id));
			} else if (
				isGoneOrderSnapshot(
					event.data as StaleOrderSnapshot,
					isClosedTab,
				)
			) {
				immediate.push({
					action: ModelEventAction.DELETE,
					id: event.id,
				});
				deletedIds.push(Number(event.id));
			} else {
				immediate.push(event);
			}
		} else {
			immediate.push(event);
			if (
				hasId &&
				INSERT_ACTIONS.has(event.action) &&
				event.data != null &&
				isGoneOrderSnapshot(
					event.data as StaleOrderSnapshot,
					isClosedTab,
				)
			) {
				immediate.push({
					action: ModelEventAction.DELETE,
					id: event.id,
				});
				deletedIds.push(Number(event.id));
			}
		}
	}
	return { hydrateIds, immediate, deletedIds, windowReset };
};

/**
 * Что делать с ответом getById. Закрытый/пустой снапшот — delete.
 * Если сервер за время запроса уже убрал заказ из таба (`stillListed`
 * false), ответ не применяем: update по неизвестному id воскресил бы строку.
 */
export type HydrateResultState = {
	/** id всё ещё в окне конструктора */
	stillListed: boolean;
	/** пока шёл getById, по WS пришёл более новый снапшот — он главнее */
	superseded?: boolean;
	/**
	 * С момента запроса строку убирали: пришёл delete по этому id или окно
	 * пересобирал INIT/regenerate. Не в окне и не убирали — это новый заказ,
	 * о котором сервер впервые сообщил пустым update (его fetch не удался).
	 */
	removedSince?: boolean;
};

export const hydrateResultEvent = (
	id: number,
	raw: StaleOrderSnapshot | null | undefined,
	isClosedTab: boolean,
	state: HydrateResultState,
): ModelSubscribeEvent | null => {
	// Более новый WS-снапшот главнее даже закрытого ответа: заказ могли
	// успеть вернуть (revert), и stale delete снёс бы живую строку.
	if (state.superseded) return null;
	if (isGoneOrderSnapshot(raw, isClosedTab)) {
		return { action: ModelEventAction.DELETE, id };
	}
	if (!state.stillListed && state.removedSince) return null;
	return {
		action: ModelEventAction.UPDATE,
		id,
		data: raw as ModelSubscribeEvent["data"],
	};
};

export type HydrateResponseKind = "order" | "missing" | "failed";

/**
 * Чем ответил getById. `createRPCQuery` с `error: true` не бросает, а
 * возвращает `{ error: true, data: RuntimeError }`; 404 — «заказа нет»,
 * любой другой сбой (таймаут, 5xx, нет соединения) — «не знаем», строку
 * трогать нельзя: иначе живой заказ удалялся бы из-за моргнувшей сети.
 */
export const hydrateResponseKind = (response: unknown): HydrateResponseKind => {
	if (response == null) return "missing";
	if (typeof response !== "object") return "failed";
	const failure = response as {
		error?: unknown;
		data?: { status?: unknown };
	};
	if (failure.error === true) {
		return Number(failure.data?.status) === 404 ? "missing" : "failed";
	}
	return "order";
};

export type HydrateCoordinatorOptions = {
	/** getById; ответ в семантике createRPCQuery с `error: true` */
	fetch: (id: number) => Promise<unknown>;
	/** false — нет соединения: не запрашивать, watchdog перепроверит */
	canFetch?: () => boolean;
	isClosedTab: () => boolean;
	/** id известен окну конструктора (видимый или скрытый) */
	has: (id: number) => boolean;
	/** окно очищено INIT/regenerate и ждёт свои ADD-чанки */
	isHydrating?: () => boolean;
	now?: () => number;
	/** текущий сырой снапшот по id из последнего эмита */
	findRaw: (id: number) => unknown;
	apply: (event: ModelSubscribeEvent) => void;
	/** сырой ответ сразу по получении — зафиксировать дедлайн оффера */
	onFetched?: (raw: unknown) => void;
	concurrency?: number;
};

export type HydrateCoordinator = {
	enqueue: (id: number) => void;
	/** Учесть батч событий. Звать ДО apply(immediate) и до enqueue. */
	noteBatch: (
		split: Pick<
			SplitOrderEvents,
			"immediate" | "deletedIds" | "windowReset"
		>,
	) => void;
	dispose: () => void;
};

export const DEFAULT_HYDRATE_CONCURRENCY = 4;
/**
 * Сколько после INIT верить `isHydrating`. Счётчик ожидаемых ADD может
 * застрять > 0, если часть тел чанка пришла пустой, а INIT бывает редко —
 * иначе новые строки через пустой update терялись бы до следующего INIT.
 */
export const HYDRATE_RESET_WINDOW_MS = 15_000;

export const createHydrateCoordinator = (
	options: HydrateCoordinatorOptions,
): HydrateCoordinator => {
	const concurrency = options.concurrency ?? DEFAULT_HYDRATE_CONCURRENCY;
	const canFetch = options.canFetch ?? (() => true);

	let disposed = false;
	let inFlight = 0;
	const queue: number[] = [];
	const queued = new Set<number>();
	const hydrating = new Set<number>();
	/** пришёл ещё один пустой update, пока запрос в полёте */
	const rehydrate = new Set<number>();
	/**
	 * Отличить «строку убрали» от «новый заказ, о котором сервер впервые
	 * сообщил пустым update». Снимается только данными по id или INIT —
	 * не стартом запроса: delete мог прийти, пока id ждал в очереди.
	 */
	const deletedIds = new Set<number>();
	let windowEpoch = 0;
	const now = options.now ?? Date.now;
	let lastResetAt = -Infinity;

	const windowStillHydrating = () =>
		Boolean(options.isHydrating?.()) &&
		now() - lastResetAt < HYDRATE_RESET_WINDOW_MS;

	const applyResult = (
		id: number,
		raw: unknown,
		before: unknown,
		epoch: number,
	) => {
		if (disposed) return;
		const event = hydrateResultEvent(
			id,
			raw as Parameters<typeof hydrateResultEvent>[1],
			options.isClosedTab(),
			{
				stillListed: options.has(id),
				// пока ждали getById, по WS пришёл более свежий снапшот
				// (в том числе первый add для строки, которой ещё не было)
				superseded: options.findRaw(id) !== before,
				// в окно, которое ещё собирается после INIT, чужие строки не
				// вставляем: нужные придут своим ADD-чанком на своём месте
				removedSince:
					deletedIds.has(id) ||
					epoch !== windowEpoch ||
					windowStillHydrating(),
			},
		);
		if (event) options.apply(event);
	};

	// eslint-disable-next-line @typescript-eslint/no-use-before-define
	const onDone = () => {
		inFlight -= 1;
		// eslint-disable-next-line @typescript-eslint/no-use-before-define
		if (!disposed) pump();
	};

	const hydrateById = async (id: number) => {
		if (!id || hydrating.has(id) || disposed) return;
		hydrating.add(id);
		const before = options.findRaw(id);
		const epoch = windowEpoch;
		try {
			if (!canFetch()) return;
			const response = await options.fetch(id);
			const kind = hydrateResponseKind(response);
			// сбой (таймаут, 5xx, нет связи) — строку не трогаем
			if (kind === "failed") return;
			const raw = kind === "missing" ? null : response;
			options.onFetched?.(raw);
			applyResult(id, raw, before, epoch);
		} catch {
			// сбой запроса — строку не трогаем
		} finally {
			hydrating.delete(id);
			// повтор нужен только в том же окне: после INIT строку принесёт
			// её ADD-чанк, лишний запрос ни к чему
			const again = rehydrate.delete(id);
			// eslint-disable-next-line @typescript-eslint/no-use-before-define
			if (again && !disposed && epoch === windowEpoch) enqueue(id);
		}
	};

	function pump() {
		while (inFlight < concurrency && queue.length && !disposed) {
			const id = queue.shift();
			if (id == null) break;
			queued.delete(id);
			if (!hydrating.has(id)) {
				inFlight += 1;
				hydrateById(id).then(onDone, onDone);
			}
		}
	}

	function enqueue(id: number) {
		if (!id || disposed) return;
		if (hydrating.has(id)) {
			rehydrate.add(id);
			return;
		}
		if (queued.has(id)) return;
		queued.add(id);
		queue.push(id);
		pump();
	}

	return {
		enqueue,
		noteBatch: ({ immediate, deletedIds: deleted, windowReset }) => {
			if (windowReset) {
				windowEpoch += 1;
				lastResetAt = now();
				deletedIds.clear();
			} else {
				// снова пришли данные по id — он уже не «удалённый»
				for (const event of immediate) {
					if (event.data != null) deletedIds.delete(Number(event.id));
				}
			}
			// delete из батча regenerate тоже считается: по такому id ещё
			// может прийти запоздалый пустой update
			deleted.forEach((id) => deletedIds.add(id));
		},
		dispose: () => {
			disposed = true;
			queue.length = 0;
			queued.clear();
			rehydrate.clear();
			deletedIds.clear();
		},
	};
};
